import React from 'react';
import EditableSelectField from './EditableSelectField';
import EditableTextField from './EditableTextField';
import EditableMultiSelectField from './EditableMultiSelectField';
import ValidatedDecimalInput from '../../../components/ValidatedDecimalInput/ValidatedDecimalInput';

// Config-driven renderer for the numbered "type group" sections (Wall/Floor/Roof,
// Window/Door). Index 1 always renders unconditionally; indices 2/3 are gated
// by `project[gatingField] > (index - 1)` — this asymmetry (group 1 has no gate at all) is
// the source's real behavior and must not be "corrected" to a uniform gate. Per-field
// config values may be a plain value or a function of the 1-based index (e.g.
// `emptyOption: (n) => n === 1`) for indices whose rendered markup genuinely diverges —
// see each tab file's config for the specific divergences this was built against.
function resolvePerIndex(configValue, n, fallback) {
  if (configValue === undefined) return fallback;
  return typeof configValue === 'function' ? configValue(n) : configValue;
}

function templateName(template, n) {
  return template.replace('{n}', n);
}

function renderField(
  field,
  n,
  { project, editable, handleInputChange, handleSelectChange, handleFieldValidityChange }
) {
  const name = templateName(field.nameTemplate, n);
  const isEditable = editable === true;

  switch (field.kind) {
    case 'select': {
      const editValueFieldName = field.editValueTemplate
        ? templateName(field.editValueTemplate, n)
        : name;
      return (
        <EditableSelectField
          name={name}
          value={project[name]}
          editValue={project[editValueFieldName]}
          onChange={handleInputChange}
          editable={isEditable}
          options={field.options}
          emptyOption={resolvePerIndex(field.emptyOption, n, true)}
          disabledFallback={field.disabledFallback ?? 'input'}
        />
      );
    }
    case 'decimal':
      return (
        <ValidatedDecimalInput
          name={name}
          value={project[name]}
          onChange={handleInputChange}
          editable={isEditable}
          unit={field.unit}
          onValidityChange={handleFieldValidityChange}
        />
      );
    case 'text':
      return (
        <EditableTextField
          name={name}
          value={project[name]}
          onChange={handleInputChange}
          editable={isEditable}
          unit={field.unit}
        />
      );
    case 'multiselect':
      return (
        <EditableMultiSelectField
          name={name}
          value={project[name]}
          onChange={(option) => handleSelectChange(name, option)}
          editable={isEditable}
          options={field.options}
        />
      );
    default:
      return null;
  }
}

export default function TypeGroupSection({
  gatingField,
  maxCount,
  fields,
  project,
  editable,
  handleInputChange,
  handleSelectChange,
  handleFieldValidityChange,
}) {
  const ctx = { project, editable, handleInputChange, handleSelectChange, handleFieldValidityChange };
  const indices = [];
  for (let n = 1; n <= maxCount; n++) {
    if (gatingField === null || n === 1 || project[gatingField] > n - 1) {
      indices.push(n);
    }
  }

  return (
    <>
      {indices.map((n) =>
        fields.map((field) => (
          <tr key={`${field.nameTemplate}-${n}`}>
            <td>{field.label}</td>
            <td>{renderField(field, n, ctx)}</td>
          </tr>
        ))
      )}
    </>
  );
}
